<template>
	<ModalErro v-if="showModal" title-modal="Erro" :message-error="messageError" @fechar="showModal = false" />
	<div class="container">
		<div class="page-header">
			<h2>Extrato</h2>
			<router-link to="/dashboard" class="btn btn-secondary">Voltar</router-link>
		</div>

		<div class="stats-cards">
			<div class="stat-card">
				<h3>{{ totalLeads }}</h3>
				<p>Total últimos 30 dias</p>
			</div>
			<div class="stat-card">
				<h3>{{ totalLeadsEnviados }}</h3>
				<p>Total sincronizado últimos 30 dias</p>
			</div>
		</div>

		<div class="linha box-data">
			<p class="linha-data">
				Data inicial:
				<input type="date" class="filtro-data" v-model="dataInicial" />
			</p>
			<p class="linha-data">
				Data Final:
				<input type="date" class="filtro-data" v-model="dataFinal" />
			</p>
			<p>
				<button @click="buscarLeads()" class="buttonBuscar">
					<i class="bi bi-search"></i>
					<span class="text-mobile">Buscar</span>
				</button>
			</p>
		</div>

		<div class="card">
			<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
				<h3>Seus Leads</h3>
				<div class="box-itens-pagina">
					<label for="itensPorPagina">Itens por página:</label>
					<select id="itensPorPagina" v-model="itensPorPagina" @change="mudarItensPorPagina"
						class="filtro-data">
						<option :value="10">10</option>
						<option :value="25">25</option>
						<option :value="50">50</option>
						<option :value="100">100</option>
					</select>
				</div>
			</div>

			<div class="table-responsive">
				<table class="candidates-table">
					<thead>
						<tr>
							<th>Nome</th>
							<th>Idade</th>
							<th>Telefone</th>
							<th>Responsável</th>
							<th>Data</th>
							<th>Status</th>
							<th>Ação</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(lead, index) in leads" :key="index">
							<td>{{ lead.Nome }}</td>
							<td>{{ lead.Idade }}</td>
							<td>{{ formatPhone(lead.Telefone) }}</td>
							<td>{{ lead.Responsavel || " - " }}</td>
							<td>
								{{
									lead.Data_criacao != "" ? formatDate(lead.Data_criacao) : "-"
								}}
							</td>
							<td>
								<span class="status" :class="{ enviado: lead.Sincronizado }">
									{{ lead.Sincronizado ? "Enviado" : "Pendente" }}
								</span>
							</td>
							<td>
								<button class="icon-reenviar" v-if="!lead.Sincronizado" @click="resendLead(lead.ID)">
									<i class="bi bi-arrow-repeat" title="Enviar para LeadCasting"
										alt="Enviar para LeadCasting"
										:class="{ 'sincronizando': leadsSincronizando[lead.ID] }"></i>
								</button>
								<span v-if="lead.Sincronizado"> - </span>
							</td>
						</tr>
					</tbody>
				</table>

				<div v-if="leads.length === 0" class="empty-state">
					<p class="margin-bottom-40px">Nenhum lead cadastrado ainda.</p>
					<router-link to="/cadastrar" class="btn btn-primary">
						Cadastrar Primeiro Lead
					</router-link>
				</div>
			</div>

			<!-- Paginação -->
			<div v-if="totalLeads > 0" class="pagination">
				<div class="pagination-info">
					Mostrando {{ inicioItem }} a {{ fimItem }} de {{ totalLeads }} registros
				</div>
				<div class="pagination-controls">
					<button @click="irParaPagina(1)" :disabled="paginaAtual === 1" class="btn-pagination">
						<i class="bi bi-chevron-double-left"></i>
					</button>
					<button @click="irParaPagina(paginaAtual - 1)" :disabled="paginaAtual === 1" class="btn-pagination">
						<i class="bi bi-chevron-left"></i>
					</button>

					<button v-for="pagina in paginasVisiveis" :key="pagina" @click="irParaPagina(pagina)"
						:class="['btn-pagination', { active: pagina === paginaAtual }]">
						{{ pagina }}
					</button>

					<button @click="irParaPagina(paginaAtual + 1)" :disabled="paginaAtual === totalPaginas"
						class="btn-pagination">
						<i class="bi bi-chevron-right"></i>
					</button>
					<button @click="irParaPagina(totalPaginas)" :disabled="paginaAtual === totalPaginas"
						class="btn-pagination">
						<i class="bi bi-chevron-double-right"></i>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { api } from "../services/api";
import ModalErro from "../components/ModalErro.vue";
import { ref, onBeforeMount, onMounted, computed } from "vue";

const leads = ref([]);
const online = ref(navigator.onLine);
const dataInicial = ref("");
const dataFinal = ref("");
const leadsSincronizando = ref({});
const showModal = ref(false);
const messageError = ref('');
const syncMessage = ref(false);

// Variáveis de paginação
const paginaAtual = ref(1);
const itensPorPagina = ref(10);
const totalLeads = ref(0);
const totalPaginas = ref(0);
const totalLeadsEnviados = ref(0);

const inicioItem = computed(() => {
	if (totalLeads.value === 0) return 0;
	return (paginaAtual.value - 1) * itensPorPagina.value + 1;
});

const fimItem = computed(() => {
	const fim = paginaAtual.value * itensPorPagina.value;
	return fim > totalLeads.value ? totalLeads.value : fim;
});

const paginasVisiveis = computed(() => {
	const paginas = [];
	const maxPaginas = 5;
	let inicio = Math.max(1, paginaAtual.value - Math.floor(maxPaginas / 2));
	let fim = Math.min(totalPaginas.value, inicio + maxPaginas - 1);

	if (fim - inicio < maxPaginas - 1) {
		inicio = Math.max(1, fim - maxPaginas + 1);
	}

	for (let i = inicio; i <= fim; i++) {
		paginas.push(i);
	}

	return paginas;
});

const formatDate = (dateString) => new Date(dateString).toLocaleDateString("pt-BR");

onMounted(() => {
	carregarLeads();
	contaLeads();
	window.addEventListener("online", updateOnlineStatus);
	window.addEventListener("offline", updateOnlineStatus);
});

onBeforeMount(() => {
	window.removeEventListener("online", updateOnlineStatus);
	window.removeEventListener("offline", updateOnlineStatus);
});

const formatPhone = (value) => {
	return value.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
};


async function contaLeads() {
	try {
		let res = await api.countLeadsScouter();
		totalLeads.value = res.total;
		totalLeadsEnviados.value = res.totalSincronizados;
	} catch (error) {
		console.log("Error" + error)
	}
}

async function carregarLeads() {
	try {
		let res = await api.getLeadsScouter(
			dataInicial.value,
			dataFinal.value,
			paginaAtual.value,
			itensPorPagina.value
		);

		leads.value = res.leads;
		// totalLeads.value = res.total;
		totalPaginas.value = res.totalPages;
		// totalLeadsEnviados.value = res.totalSincronizados;

		// Calcula total de leads sincronizados da página atual
		const sincronizadosPaginaAtual = res.leads.filter((c) => c.Sincronizado == 1).length;

	} catch (error) {
		console.log("Error", error);
	} finally {
		setTimeout(() => (syncMessage.value = ""), 5000);
	}
}

// Função chamada pelo botão de busca
function buscarLeads() {
	paginaAtual.value = 1; // Reset para primeira página ao fazer nova busca
	carregarLeads();
}

// Função chamada ao mudar quantidade de itens por página
function mudarItensPorPagina() {
	paginaAtual.value = 1; // Reset para primeira página
	carregarLeads();
}

async function resendLead(id) {
	try {
		leadsSincronizando[id] = true;
		await api.reSendLead(id);
		leads.value.forEach((lead) => { if (lead.ID == id) lead.Sincronizado = 1 });
	} catch (error) {
		showModal.value = true;
		messageError.value = error.response.data.message;
	} finally {
		leadsSincronizando[id] = false;
	}
}

function irParaPagina(pagina) {
	if (pagina >= 1 && pagina <= totalPaginas.value && pagina !== paginaAtual.value) {
		paginaAtual.value = pagina;
		carregarLeads();
	}
}

function updateOnlineStatus() {
	online.value = navigator.onLine;
}
</script>

<style scoped>
.box-itens-pagina {
	display: flex;
	flex-direction: row;
	gap: 5px;
	align-items: center;
}
</style>